// Dates and times in the viewer's own time zone. Plan limits reset at a fixed
// instant (midnight UTC for yearly plans, the Stripe anchor for monthly ones),
// so a date shown in UTC can be a different day for the viewer: a reset at
// "September 14" was 8 PM on September 13 in New York, and a customer who
// planned around the date lost the credits she meant to use that evening.

type DateInput = string | number | Date;

const toDate = (value: DateInput) => new Date(value);

const isValid = (date: Date) => !Number.isNaN(date.getTime());

/** "November 13, 2026 at 8:00 PM EST", in the viewer's time zone. */
export function formatLocalDateTime(value: DateInput, locale = 'en-US') {
  const date = toDate(value);
  if (!isValid(date)) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

/** "Sep 13, 2026, 9:05 PM", for item details such as "Created At". */
export function formatShortLocalDateTime(value: DateInput, locale = 'en-US') {
  const date = toDate(value);
  if (!isValid(date)) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

/** The same date the server renders: UTC, no time. */
export function formatUtcDate(value: DateInput) {
  const date = toDate(value);
  if (!isValid(date)) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** "in 30 days", "in 5 hours", "in 12 minutes" (null once it has passed). */
export function formatTimeUntil(
  value: DateInput,
  locale = 'en-US',
  now: number = Date.now(),
) {
  const date = toDate(value);
  if (!isValid(date)) return null;
  const diffMs = date.getTime() - now;
  if (diffMs <= 0) return null;
  const minutes = Math.round(diffMs / 60000);
  const hours = Math.round(diffMs / 3600000);
  const days = Math.round(diffMs / 86400000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
    if (minutes < 60) return rtf.format(Math.max(1, minutes), 'minute');
    if (hours < 48) return rtf.format(hours, 'hour');
    return rtf.format(days, 'day');
  } catch {
    return null;
  }
}
